import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const SubmissionsTable = ({
  submissions,
  labels,
  selectedSubmissions,
  onSelectionChange,
  onViewDetails,
  searchTerm
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const itemsPerPage = 20;

  const filteredSubmissions = useMemo(() => {
    let filtered = submissions;

    if (searchTerm) {
      filtered = filtered?.filter(s => {
        const searchLower = searchTerm?.toLowerCase();
        const formTitle = s?.forms?.title?.toLowerCase() || '';
        const submissionData = JSON.stringify(s?.submissionData || {})?.toLowerCase();
        return formTitle?.includes(searchLower) || submissionData?.includes(searchLower);
      });
    }

    // Sort
    filtered = [...filtered]?.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];

      if (sortField === 'formName') {
        aVal = a?.forms?.title || '';
        bVal = b?.forms?.title || '';
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [submissions, searchTerm, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredSubmissions?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = filteredSubmissions?.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      onSelectionChange(paginatedSubmissions?.map(s => s?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedSubmissions?.includes(id)) {
      onSelectionChange(selectedSubmissions?.filter(sid => sid !== id));
    } else {
      onSelectionChange([...selectedSubmissions, id]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-500/10 text-blue-500';
      case 'reviewed':
        return 'bg-success/10 text-success';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      case 'flagged':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    return sortDirection === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <input
                  type="checkbox"
                  checked={paginatedSubmissions?.length > 0 && paginatedSubmissions?.every(s => selectedSubmissions?.includes(s?.id))}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('formName')}
              >
                <div className="flex items-center gap-2">
                  Form Name
                  <SortIcon field="formName" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center gap-2">
                  Date
                  <SortIcon field="createdAt" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-2">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Labels
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedSubmissions?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center">
                  <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No submissions found</p>
                </td>
              </tr>
            ) : (
              paginatedSubmissions?.map((submission) => (
                <tr key={submission?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions?.includes(submission?.id)}
                      onChange={() => handleSelectOne(submission?.id)}
                      className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        {submission?.forms?.title || 'Unknown Form'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(submission?.createdAt)}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${getStatusColor(submission?.status || 'new')}`}>
                      {submission?.status || 'new'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {submission?.source || 'Direct'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {submission?.submissionLabelAssignments?.slice(0, 3)?.map((assignment) => (
                        <span
                          key={assignment?.labelId}
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                          style={{
                            backgroundColor: `${assignment?.submissionLabels?.color}20`,
                            color: assignment?.submissionLabels?.color
                          }}
                        >
                          {assignment?.submissionLabels?.name}
                        </span>
                      ))}
                      {submission?.submissionLabelAssignments?.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                          +{submission?.submissionLabelAssignments?.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewDetails(submission)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredSubmissions?.length)} of {filteredSubmissions?.length} results
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            />
            <span className="text-sm text-foreground px-3">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsTable;